import { useQuery } from "@apollo/client";
import {
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MapPinIcon,
  PhotoIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";
import moment from "moment";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  FETCH_INSTANCE_LOGISTICS_EXHIBITIONS,
  FETCH_INSTANCE_LOGISTICS_JOBS,
  FETCH_INSTANCE_LOGISTICS_RESERVATIONS,
} from "../../../../../../../graphql/query/InstanceLogistics";
import useUser from "../../../../../../../hooks/useUser";
import getInstanceLogisticsStatus, {
  getInstanceLocationDisplay,
} from "../../../../../logisticsStatus";
import {
  buildLogisticsEvents,
  eventsOnDate,
  formatShipmentEndpoint,
  getMonthDays,
} from "./instanceLogisticsCalendar";
import InstanceConditionStatus from "./InstanceConditionStatus";

const eventStyles = {
  solo: {
    background: "bg-sky-500",
    badge: "bg-sky-100 text-sky-800",
    label: "Solo",
  },
  group: {
    background: "bg-emerald-500",
    badge: "bg-emerald-100 text-emerald-800",
    label: "Group",
  },
  "art-fair": {
    background: "bg-rose-500",
    badge: "bg-rose-100 text-rose-800",
    label: "Art Fair",
  },
  "viewing-room": {
    background: "bg-violet-500",
    badge: "bg-violet-100 text-violet-800",
    label: "Viewing Room",
  },
  transit: {
    background: "bg-cyan-500",
    badge: "bg-cyan-100 text-cyan-800",
    label: "In Transit",
  },
  job: {
    background: "bg-amber-500",
    badge: "bg-amber-100 text-amber-800",
    label: "Job",
  },
  reservation: {
    background: "bg-indigo-500",
    badge: "bg-indigo-100 text-indigo-800",
    label: "Reservation",
  },
};

const logisticsStatusStyles = {
  "at-location": "bg-emerald-100 text-emerald-800",
  "booked-out": "bg-gray-100 text-gray-700",
  delivered: "bg-teal-100 text-teal-800",
  "external-storage": "bg-gray-100 text-gray-700",
  "in-transit": "bg-indigo-100 text-indigo-800",
  "location-unconfirmed": "bg-gray-200 text-gray-700",
  unlocated: "bg-gray-100 text-gray-600",
  upcoming: "bg-blue-100 text-blue-800",
};

const EventBar = ({ dateKey, event }) => {
  const className = `block h-1.5 w-full ${eventStyles[event.type].background} ${
    event.date === dateKey ? "rounded-l-full" : ""
  } ${event.endDate === dateKey ? "rounded-r-full" : ""} ${
    event.isCompleted ? "opacity-40" : ""
  }`;

  return (
    <span
      className={className}
      title={event.label}
    />
  );
};

const LogisticsCalendar = ({
  events,
  month,
  onChangeMonth,
  onSelectEvents,
  selectedDate,
}) => {
  const days = useMemo(() => getMonthDays(month), [month]);

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <button
          aria-label="Previous month"
          onClick={() => onChangeMonth(moment(month).subtract(1, "month"))}
          type="button"
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-1.5 text-sm font-bold">
          <CalendarDaysIcon className="h-4 w-4" />
          {moment(month).format("MMMM YYYY")}
        </div>
        <button
          aria-label="Next month"
          onClick={() => onChangeMonth(moment(month).add(1, "month"))}
          type="button"
        >
          <ChevronRightIcon className="h-4 w-4" />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-7 text-center text-[10px] font-medium uppercase text-gray-400">
        {["S", "M", "T", "W", "T", "F", "S"].map((day, index) => (
          <div key={`${day}-${index}`}>{day}</div>
        ))}
      </div>
      <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-7">
        {days.map(day => {
          const dayEvents = eventsOnDate(events, day.dateKey);
          return (
            <button
              key={day.dateKey}
              aria-label={`Show events for ${day.dateKey}`}
              className={`aspect-square border border-gray-100 p-0.5 text-left align-top hover:bg-sky-50 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-sky-600 ${
                day.isCurrentMonth
                  ? "text-gray-700"
                  : "bg-gray-50 text-gray-300"
              } ${day.isToday ? "ring-1 ring-inset ring-sky-600" : ""} ${
                selectedDate === day.dateKey ? "bg-sky-50" : ""
              }`}
              onClick={() => onSelectEvents(dayEvents, day.dateKey)}
              title={dayEvents.map(event => event.label).join("\n")}
              type="button"
            >
              <span className="block w-full text-[10px]">
                {day.date.date()}
              </span>
              <span className="mt-0.5 block space-y-0.5">
                {dayEvents.slice(0, 3).map(event => (
                  <EventBar
                    key={event.id}
                    dateKey={day.dateKey}
                    event={event}
                  />
                ))}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const InstanceLogisticsStatus = ({ instance }) => {
  const { hasPermission } = useUser();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [month, setMonth] = useState(moment().startOf("month"));
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const instanceId = instance?.id;
  const currentMove = instance?.currentWarehouseMove;
  const logisticsStatus = getInstanceLogisticsStatus(currentMove);
  const isInTransit = logisticsStatus.kind === "in-transit";
  const currentLocation = getInstanceLocationDisplay(currentMove);
  const hasKnownLocation = [
    "at-location",
    "booked-out",
    "external-storage",
  ].includes(logisticsStatus.kind);
  const shipmentLabel = currentMove?.shipment?.id
    ? `${formatShipmentEndpoint(currentMove.shipment, "ori")} → ${formatShipmentEndpoint(currentMove.shipment, "del")}`
    : null;
  const instanceFilter = {
    id: { operator: "eq", value: instanceId },
  };

  const { data: jobData } = useQuery(FETCH_INSTANCE_LOGISTICS_JOBS, {
    skip: !instanceId,
    variables: {
      input: {
        _jobItem: { _instance: instanceFilter },
        limit: 100,
        sort1: { operator: "ASC", value: "dueDate" },
      },
    },
  });
  const { data: exhibitionData } = useQuery(
    FETCH_INSTANCE_LOGISTICS_EXHIBITIONS,
    {
      skip: !instanceId,
      variables: {
        input: {
          _exhibitionWork: { _instance: instanceFilter },
          limit: 100,
          type: {
            operator: "in",
            value: ["Solo", "Group", "Artfair", "Viewing Room"],
          },
        },
      },
    },
  );
  const { data: reservationData } = useQuery(
    FETCH_INSTANCE_LOGISTICS_RESERVATIONS,
    {
      skip: !instanceId || !hasPermission("READ_RESERVATION"),
      variables: {
        input: {
          _instance: instanceFilter,
          limit: 100,
        },
      },
    },
  );
  const jobs = useMemo(() => jobData?.jobsV2?.edges || [], [jobData]);
  const openJobs = useMemo(
    () => jobs.filter(job => ![3, 4].includes(job.statusId)),
    [jobs],
  );
  const exhibitions = useMemo(
    () => exhibitionData?.exhibitionsV2?.edges || [],
    [exhibitionData],
  );
  const reservationInstances = useMemo(
    () => reservationData?.reservationInstancesV2?.edges || [],
    [reservationData],
  );
  const events = useMemo(
    () =>
      buildLogisticsEvents({
        exhibitions,
        jobs,
        reservationInstances,
        transit: isInTransit
          ? {
              ...currentMove,
              instanceId,
              label: shipmentLabel,
            }
          : null,
      }),
    [
      currentMove,
      exhibitions,
      instanceId,
      isInTransit,
      jobs,
      reservationInstances,
      shipmentLabel,
    ],
  );
  const jobSummary = openJobs
    .map(
      job =>
        `${job.jobType?.description || job.description || `Job ${job.id}`}: ${moment(job.dueDate).format("D MMM YYYY")}`,
    )
    .join("\n");

  const selectEvents = (dayEvents, dateKey) => {
    setSelectedEvents(dayEvents);
    setSelectedDate(dateKey || dayEvents[0]?.date || null);
  };

  return (
    <div
      className="relative inline-flex flex-wrap items-center gap-1.5 text-xs font-normal"
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setIsCalendarOpen(false);
        }
      }}
    >
      {hasKnownLocation ? (
        <Link
          className={`inline-flex items-center gap-1 rounded-full px-2 py-1 hover:underline ${logisticsStatusStyles[logisticsStatus.kind]}`}
          title={currentLocation.summary}
          to={`/instance/${instanceId}/warehouse`}
        >
          <MapPinIcon className="h-3.5 w-3.5" />
          {currentLocation.summary}
        </Link>
      ) : (
        <Link
          className={`inline-flex max-w-[360px] items-center gap-1 rounded-full px-2 py-1 hover:underline ${logisticsStatusStyles[logisticsStatus.kind]}`}
          title={
            shipmentLabel
              ? `${logisticsStatus.label}: ${shipmentLabel}`
              : logisticsStatus.label
          }
          to={`/instance/${instanceId}/warehouse`}
        >
          <TruckIcon className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">
            {logisticsStatus.label}
            {shipmentLabel ? `: ${shipmentLabel}` : ""}
          </span>
        </Link>
      )}
      <InstanceConditionStatus condition={instance?.condition} />
      {instance?.framed === 0 && (
        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-slate-700">
          <PhotoIcon className="h-3.5 w-3.5" />
          Unframed
        </span>
      )}
      <div
        className="group relative order-first inline-flex items-center gap-1.5"
        onMouseLeave={() => setIsCalendarOpen(false)}
      >
        <button
          aria-expanded={isCalendarOpen}
          className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1 text-gray-700"
          onClick={() => setIsCalendarOpen(isOpen => !isOpen)}
          type="button"
        >
          <CalendarDaysIcon className="h-3.5 w-3.5" />
          Calendar
        </button>

        <div
          className={`absolute left-0 top-full z-50 w-[570px] max-w-[calc(100vw-2rem)] pt-2 group-hover:block ${
            isCalendarOpen ? "block" : "hidden"
          }`}
        >
          <div className="rounded-xl border border-gray-200 bg-white p-4 text-gray-800 shadow-xl">
            <div className="mb-3 flex items-start gap-3">
              <div className="flex flex-wrap gap-2">
                {Object.entries(eventStyles).map(([type, style]) => (
                  <span
                    key={type}
                    className={`inline-flex items-center gap-1 text-[10px] ${style.badge} rounded-full px-1.5 py-0.5`}
                  >
                    <span
                      className={`h-1.5 w-3 rounded-full ${style.background}`}
                    />
                    {style.label}
                  </span>
                ))}
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_190px]">
              <LogisticsCalendar
                events={events}
                month={month}
                onChangeMonth={setMonth}
                onSelectEvents={selectEvents}
                selectedDate={selectedDate}
              />
              <aside className="min-w-0 border-gray-200 sm:border-l sm:pl-3">
                {selectedEvents.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    <div className="text-xs font-bold text-gray-700">
                      {moment(selectedDate).format("D MMMM YYYY")}
                    </div>
                    {selectedEvents.map(event => (
                      <div
                        key={event.id}
                        className={`flex flex-col gap-1 rounded px-2 py-1.5 text-[11px] ${eventStyles[event.type].badge} ${
                          event.isCompleted ? "opacity-60" : ""
                        }`}
                      >
                        <span className="font-bold">
                          {eventStyles[event.type].label}
                        </span>
                        {event.href ? (
                          <Link
                            className="font-medium hover:underline"
                            to={event.href}
                          >
                            {event.label}
                          </Link>
                        ) : (
                          <span className="font-medium">{event.label}</span>
                        )}
                        <span>
                          {moment(event.date).format("D MMM YYYY")}
                          {event.endDate !== event.date
                            ? ` – ${moment(event.endDate).format("D MMM YYYY")}`
                            : ""}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    className="text-[11px] text-gray-400"
                    aria-live="polite"
                  >
                    Select a day to see everything scheduled.
                  </div>
                )}
              </aside>
            </div>
          </div>
        </div>
      </div>
      {openJobs.length > 0 && (
        <Link
          className="rounded-full bg-amber-100 px-2 py-1 text-amber-800 hover:underline"
          title={jobSummary}
          to={`/instance/${instanceId}/jobs`}
        >
          {openJobs.length} open job{openJobs.length === 1 ? "" : "s"}
        </Link>
      )}
    </div>
  );
};

export default InstanceLogisticsStatus;
